<template>
  <div v-if="shouldShowPopup" class="revival-overlay" @click.self="closePopup">
    <div class="revival-container">
      <!-- Close Button -->
      <button class="close-button" @click="closePopup">
        <i class="material-icons">close</i>
      </button>

      <!-- Revival Content -->
      <div class="revival-content">
        <!-- Death Icon -->
        <div class="death-icon">
          <i class="material-icons">sentiment_very_dissatisfied</i>
        </div>

        <h2 class="revival-title">⚰️ FIGHTER PERDIDO</h2>

        <!-- Dead Fighter Info -->
        <div v-if="currentFighter" class="dead-fighter-info">
          <div class="fighter-avatar">
            <img 
              v-if="currentFighter.photo" 
              :src="getFighterPhotoUrl(currentFighter.photo)" 
              :alt="currentFighter.name"
              @error="handleImageError"
            />
            <div v-else class="no-photo">
              <i class="material-icons">person</i>
            </div>
          </div>
          
          <div class="fighter-details">
            <h3 class="fighter-name">{{ currentFighter.name }}</h3>
            <div class="fighter-stats">
              <span class="stat">
                <i class="material-icons">star</i>
                Nível {{ currentFighter.level || 1 }}
              </span>
              <span class="stat">
                <i class="material-icons">local_fire_department</i>
                {{ currentFighter.rank || 'F' }} Rank
              </span>
            </div>
            <p class="death-message">
              Seu fighter morreu em combate e precisa ser reconstruído!
            </p>
          </div>
        </div>

        <!-- Revival Items Available -->
        <div class="revival-items-section">
          <h4>🔬 Items de Reconstrução Disponíveis</h4>
          
          <div v-if="availableRevivalItems.length === 0" class="no-items">
            <i class="material-icons">inventory_2</i>
            <p>Você não possui items de reconstrução.</p>
            <p class="hint">Adquira nanites na loja premium!</p>
          </div>
          
          <div v-else class="items-grid">
            <div 
              v-for="item in availableRevivalItems" 
              :key="item.id"
              class="revival-item"
              :class="{ selected: selectedItemId === item.id }"
              @click="selectRevivalItem(item.id)"
            >
              <div class="item-icon">
                <i class="material-icons">biotech</i>
              </div>
              
              <div class="item-info">
                <h5>{{ item.name }}</h5>
                <p class="item-description">{{ item.description }}</p>
                
                <div class="item-stats">
                  <div class="success-rate">
                    <span class="label">Taxa de Sucesso:</span>
                    <span class="rate">{{ getRevivalChance(item) }}%</span>
                    <div class="chance-bar">
                      <div 
                        class="fill" 
                        :style="{ width: getRevivalChance(item) + '%' }"
                      ></div>
                    </div>
                  </div>
                  
                  <div class="quantity">
                    <i class="material-icons">inventory</i>
                    <span>{{ item.remainingUses }}x disponível</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Revival Actions -->
        <div class="revival-actions">
          <button 
            v-if="availableRevivalItems.length > 0"
            @click="attemptRevival" 
            :disabled="isOnCooldown || loading || !selectedItemId"
            class="revival-button"
            :class="{ cooldown: isOnCooldown }"
          >
            <i class="material-icons">
              {{ loading ? 'hourglass_empty' : isOnCooldown ? 'timer' : 'biotech' }}
            </i>
            <span v-if="loading">Processando...</span>
            <span v-else-if="isOnCooldown">Cooldown ({{ cooldownTimeLeft }}s)</span>
            <span v-else>🔬 Tentar Reconstrução</span>
          </button>
          
          <button @click="openPremiumShop" class="shop-button" v-if="availableRevivalItems.length === 0">
            <i class="material-icons">shopping_cart</i>
            Ir para Loja Premium
          </button>
          
          <button @click="closePopup" class="accept-loss-button">
            <i class="material-icons">thumb_down</i>
            Aceitar Perda
          </button>
        </div>

        <!-- Don't Show Again Option -->
        <div class="show-options">
          <label class="checkbox-container">
            <input 
              type="checkbox" 
              v-model="dontShowToday"
              @change="handleDontShowAgain"
            />
            <span class="checkmark"></span>
            Não mostrar novamente hoje
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'RevivalPopup',
  
  data() {
    return {
      selectedItemId: null,
      dontShowToday: false,
      intervalId: null
    };
  },

  computed: {
    ...mapState('revival', [
      'deadFighters', 
      'availableRevivalItems', 
      'loading', 
      'error'
    ]),
    
    ...mapGetters('revival', [
      'shouldShowRevivalPopup', 
      'isOnCooldown', 
      'cooldownTimeLeft',
      'currentTargetFighter'
    ]),

    shouldShowPopup() {
      return this.shouldShowRevivalPopup;
    },

    currentFighter() {
      // Priority: target fighter > first dead fighter
      return this.currentTargetFighter || (this.deadFighters.length > 0 ? this.deadFighters[0] : null);
    }
  },

  watch: {
    shouldShowPopup(newVal) {
      if (newVal) {
        // Auto-select first revival item if available
        if (this.availableRevivalItems.length > 0 && !this.selectedItemId) {
          this.selectedItemId = this.availableRevivalItems[0].id;
        }
        
        // Start countdown timer for cooldown
        this.startCooldownTimer();
      } else {
        this.stopCooldownTimer();
      }
    },

    availableRevivalItems(newItems) {
      // Auto-select first item if none selected
      if (newItems.length > 0 && !this.selectedItemId) {
        this.selectedItemId = newItems[0].id;
      } else if (newItems.length === 0) {
        this.selectedItemId = null;
      }
    }
  },

  mounted() {
    // Check revival status on component mount
    this.checkRevivalStatus();
  },

  beforeUnmount() {
    this.stopCooldownTimer();
  },

  methods: {
    ...mapActions('revival', [
      'attemptRevival',
      'closeRevivalPopup', 
      'dontShowRevivalToday',
      'checkRevivalStatus',
      'clearError'
    ]),

    selectRevivalItem(itemId) {
      this.selectedItemId = itemId;
    },

    async attemptRevival() {
      if (!this.selectedItemId || !this.currentFighter) {
        return;
      }

      try {
        await this.attemptRevival({
          fighterId: this.currentFighter.id,
          revivalItemId: this.selectedItemId
        });

        // If successful, item quantity will be updated by store
        // If no more items, popup will close automatically
        
      } catch (error) {
        console.error('Revival attempt failed:', error);
        // Error notification handled by store action
      }
    },

    closePopup() {
      this.closeRevivalPopup();
      this.selectedItemId = null;
    },

    handleDontShowAgain() {
      if (this.dontShowToday) {
        this.dontShowRevivalToday();
      }
    },

    openPremiumShop() {
      this.closePopup();
      // Emit event to open premium shop (handled by App.vue)
      this.$emit('open-premium-shop');
    },

    getRevivalChance(item) {
      // O servidor já entrega a chance calculada (GET /players/:id/revival-items);
      // antes isto olhava item.premiumItem.effectData, que aquela rota nunca devolveu.
      return item.revivalChance || 50;
    },

    getFighterPhotoUrl(photo) {
      if (!photo) return '';
      
      // Handle different photo URL formats
      if (photo.startsWith('http')) {
        return photo;
      }
      
      const API_URL = process.env.VUE_APP_STRAPI_URL || process.env.VUE_APP_API_URL || 'http://localhost:3000';
      return photo.startsWith('/') ? `${API_URL}${photo}` : `${API_URL}/${photo}`;
    },

    handleImageError(event) {
      // Hide broken image and show placeholder
      event.target.style.display = 'none';
      const placeholder = event.target.parentNode.querySelector('.no-photo');
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },

    startCooldownTimer() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      
      this.intervalId = setInterval(() => {
        if (!this.isOnCooldown) {
          this.stopCooldownTimer();
        }
      }, 1000);
    },

    stopCooldownTimer() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }
};
</script>

<style scoped lang="scss">
// Cyberpunk Colors
$primary-red: #ff4646;
$primary-blue: #3498db;
$dark-bg: #1a1a1a;
$darker: #0d0d0d;
$gold: #ffd700;
$danger: #ff4444;
$success: #4caf50;

.revival-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 19000; // Lower than promotion (20000)
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.revival-container {
  background: linear-gradient(135deg, $darker 0%, $dark-bg 100%);
  border: 2px solid $danger;
  border-radius: 12px;
  box-shadow: 
    0 0 40px rgba($danger, 0.5),
    0 0 80px rgba($danger, 0.2);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 30px;
  animation: slideUp 0.3s ease;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, $danger, darken($danger, 20%));
    border-radius: 4px;
    
    &:hover {
      background: linear-gradient(180deg, lighten($danger, 10%), $danger);
    }
  }
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba($danger, 0.2);
  border: 2px solid $danger;
  color: $danger;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: rgba($danger, 0.4);
    transform: scale(1.1);
  }
  
  i {
    font-size: 18px;
  }
}

.revival-content {
  text-align: center;
  
  .death-icon {
    margin-bottom: 20px;
    
    i {
      font-size: 60px;
      color: $danger;
      opacity: 0.8;
      animation: pulse 2s infinite;
    }
  }
  
  .revival-title {
    color: $danger;
    font-family: 'Cinzel Decorative', serif;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 30px 0;
    text-shadow: 0 0 20px rgba($danger, 0.5);
  }
}

.dead-fighter-info {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba($danger, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
  text-align: left;
  
  .fighter-avatar {
    flex-shrink: 0;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 3px solid $danger;
    overflow: hidden;
    position: relative;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .no-photo {
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, $darker, $dark-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      color: $danger;
      
      i {
        font-size: 40px;
        opacity: 0.7;
      }
    }
  }
  
  .fighter-details {
    flex: 1;
    
    .fighter-name {
      color: $primary-red;
      font-size: 1.3rem;
      font-weight: 700;
      margin: 0 0 10px 0;
      font-family: 'Orbitron', sans-serif;
    }
    
    .fighter-stats {
      display: flex;
      gap: 15px;
      margin-bottom: 10px;
      
      .stat {
        display: flex;
        align-items: center;
        gap: 5px;
        color: #ccc;
        font-size: 0.9rem;
        
        i {
          font-size: 16px;
          color: $gold;
        }
      }
    }
    
    .death-message {
      color: #aaa;
      margin: 0;
      font-style: italic;
    }
  }
}

.revival-items-section {
  margin-bottom: 30px;
  
  h4 {
    color: $primary-blue;
    font-family: 'Orbitron', sans-serif;
    font-size: 1.2rem;
    margin-bottom: 20px;
    text-align: center;
  }
  
  .no-items {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    
    i {
      font-size: 50px;
      color: #666;
      margin-bottom: 15px;
    }
    
    p {
      margin: 10px 0;
    }
    
    .hint {
      color: $primary-blue;
      font-weight: 600;
    }
  }
  
  .items-grid {
    display: flex;
    flex-direction: column;
    gap: 15px;
    
    .revival-item {
      background: rgba(0, 0, 0, 0.3);
      border: 2px solid transparent;
      border-radius: 8px;
      padding: 15px;
      cursor: pointer;
      transition: all 0.3s;
      display: flex;
      align-items: center;
      gap: 15px;
      
      &:hover {
        border-color: rgba($primary-blue, 0.5);
        background: rgba($primary-blue, 0.1);
      }
      
      &.selected {
        border-color: $primary-blue;
        background: rgba($primary-blue, 0.2);
        box-shadow: 0 0 20px rgba($primary-blue, 0.3);
      }
      
      .item-icon {
        flex-shrink: 0;
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, $primary-blue, darken($primary-blue, 20%));
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        i {
          font-size: 24px;
          color: white;
        }
      }
      
      .item-info {
        flex: 1;
        text-align: left;
        
        h5 {
          color: $primary-blue;
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 5px 0;
          font-family: 'Orbitron', sans-serif;
        }
        
        .item-description {
          color: #ccc;
          font-size: 0.85rem;
          margin: 0 0 10px 0;
          opacity: 0.9;
        }
        
        .item-stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          
          .success-rate {
            flex: 1;
            
            .label {
              color: #999;
              font-size: 0.8rem;
              display: block;
              margin-bottom: 3px;
            }
            
            .rate {
              color: $success;
              font-weight: 700;
              font-size: 0.9rem;
            }
            
            .chance-bar {
              width: 80px;
              height: 6px;
              background: rgba(0, 0, 0, 0.5);
              border-radius: 3px;
              overflow: hidden;
              margin-top: 5px;
              
              .fill {
                height: 100%;
                background: linear-gradient(90deg, $success, lighten($success, 20%));
                transition: width 0.3s ease;
              }
            }
          }
          
          .quantity {
            display: flex;
            align-items: center;
            gap: 5px;
            color: #ccc;
            font-size: 0.85rem;
            
            i {
              font-size: 16px;
              color: $gold;
            }
          }
        }
      }
    }
  }
}

.revival-actions {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
  
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px 20px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s;
    border: none;
    font-family: 'Rajdhani', sans-serif;
    
    i {
      font-size: 20px;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }
  }
  
  .revival-button {
    background: linear-gradient(45deg, $success, darken($success, 10%));
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba($success, 0.4);
    }
    
    &.cooldown {
      background: linear-gradient(45deg, #666, #555);
      
      i {
        animation: spin 1s linear infinite;
      }
    }
  }
  
  .shop-button {
    background: linear-gradient(45deg, $primary-blue, darken($primary-blue, 10%));
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba($primary-blue, 0.4);
    }
  }
  
  .accept-loss-button {
    background: transparent;
    border: 2px solid #666;
    color: #999;
    
    &:hover {
      border-color: $danger;
      color: $danger;
      background: rgba($danger, 0.1);
    }
  }
}

.show-options {
  text-align: center;
  
  .checkbox-container {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    color: #ccc;
    font-size: 0.9rem;
    user-select: none;
    
    input[type="checkbox"] {
      position: relative;
      width: 20px;
      height: 20px;
      margin: 0;
      opacity: 0;
      cursor: pointer;
    }
    
    .checkmark {
      position: absolute;
      width: 20px;
      height: 20px;
      background: transparent;
      border: 2px solid #666;
      border-radius: 3px;
      transition: all 0.3s;
      
      &:after {
        content: '';
        position: absolute;
        display: none;
        left: 6px;
        top: 2px;
        width: 6px;
        height: 10px;
        border: solid $primary-blue;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }
    
    input:checked ~ .checkmark {
      border-color: $primary-blue;
      background: rgba($primary-blue, 0.2);
      
      &:after {
        display: block;
      }
    }
    
    &:hover .checkmark {
      border-color: $primary-blue;
    }
  }
}

// Animations
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes pulse {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 0.4; }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Mobile Responsiveness
@media (max-width: 768px) {
  .revival-container {
    padding: 20px;
    margin: 10px;
  }
  
  .revival-title {
    font-size: 1.4rem;
  }
  
  .dead-fighter-info {
    flex-direction: column;
    text-align: center;
    
    .fighter-details {
      .fighter-stats {
        justify-content: center;
      }
    }
  }
  
  .revival-items-section {
    .items-grid {
      .revival-item {
        .item-stats {
          flex-direction: column;
          gap: 10px;
          
          .success-rate {
            text-align: center;
          }
        }
      }
    }
  }
}</style>